import {
  CloseOutlined,
  ContainerOutlined,
  EditOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Button, Form, Input, Modal, Switch, Table } from 'antd'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import OrderStatus from '@/components/Orders/OrderStatus/OrderStatus'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { ConstructionSiteInterface, OrderShortType } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'

import { columns } from './ConstructionSite.config'
import cn from './ConstructionSite.module.scss'

function ConstructionSite() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [modal, contextHolder] = Modal.useModal()
  const { organisation, selectedOrganisation } = useUserInfoStore()

  const [data, setData] = useState<ConstructionSiteInterface | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()

  const orgId = selectedOrganisation?.id || organisation?.id
  const isOrgSelected = Object.keys(selectedOrganisation || {}).length > 0

  const fetchData = () => {
    if (!orgId || !id) return
    setLoading(true)
    api
      .get(`/organisations/${orgId}/construction-sites/${id}`)
      .then((res) => {
        const siteData = res.data.details || res.data
        setData(siteData)
        form.setFieldsValue({
          description: siteData.constructionSiteModel.description,
          address: siteData.constructionSiteModel.address,
          ownerName: siteData.constructionSiteModel.ownerName,
          ownerPhoneNumber: siteData.constructionSiteModel.ownerPhoneNumber,
          isActive: siteData.constructionSiteModel.isActive,
          isMain: siteData.constructionSiteModel.isMain,
          isStore: siteData.constructionSiteModel.isStore,
        })
      })
      .catch((err) => handleApiError(err, modal))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchData()
  }, [id, orgId])

  const handleSave = async (
    values: Partial<ConstructionSiteInterface['constructionSiteModel']>
  ) => {
    setSubmitting(true)
    try {
      const res = await api.put(
        `/organisations/${orgId}/construction-sites/${id}`,
        values
      )
      toast.success('Данные объекта успешно обновлены')
      setIsEditing(false)
      const updatedSite = res.data.constructionSite || res.data
      setData((prev) =>
        prev ? { ...prev, constructionSiteModel: updatedSite } : null
      )
    } catch (err) {
      handleApiError(err, modal)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <Loader isModalOpen={true} loaderType="modal" />
  if (!data) return null

  // Компонент карточки для мобильной версии заказов
  const OrderCard = ({ order }: { order: OrderShortType }) => (
    <div className={cn.orderCard}>
      <div className={cn.cardHeader}>
        <Link to={`/profile/orders/${order.code}`} className={cn.orderNum}>
          № {order.code}
        </Link>
        <span className={cn.orderDate}>
          {new Date(order.createdAt).toLocaleDateString('ru-RU')}
        </span>
      </div>
      <div className={cn.cardBody}>
        <div className={cn.cardRow}>
          <span className={cn.cardLabel}>Статус:</span>
          <div className={cn.cardValue}>
            <OrderStatus order={order} />
          </div>
        </div>
        <div className={cn.cardRow}>
          <span className={cn.cardLabel}>Сумма:</span>
          <span className={cn.cardValue}>
            {order.totalPrice != null
              ? trimTrailingZeros(order.totalPrice)
              : '0'}{' '}
            BYN
          </span>
        </div>
        {order.comment && (
          <div className={cn.cardRow}>
            <span className={cn.cardLabel}>Комментарий:</span>
            <span className={cn.cardValue}>{order.comment}</span>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <section className={cn.constructionSiteInfo}>
      {contextHolder}
      <div className={cn.inner}>
        <div className={cn.header}>
          <div className={cn.back} onClick={() => navigate(-1)}>
            <LeftOutlined /> Назад к списку объектов
          </div>
          <div className={cn.headerContent}>
            <h1 className={cn.title}>
              {data.constructionSiteModel.description}
            </h1>
            {!isEditing ? (
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
                className={cn.editIcon}
              >
                Редактировать
              </Button>
            ) : (
              <div className={cn.editActions}>
                <Button
                  onClick={() => setIsEditing(false)}
                  icon={<CloseOutlined />}
                  className={cn.cancelBtn}
                >
                  Отмена
                </Button>
                <Button
                  type="primary"
                  onClick={() => form.submit()}
                  loading={submitting}
                  icon={<SaveOutlined />}
                  className={cn.saveBtn}
                >
                  Сохранить
                </Button>
              </div>
            )}
          </div>
        </div>

        <Form
          form={form}
          onFinish={handleSave}
          layout="vertical"
          disabled={submitting}
        >
          <div className={cn.infoCard}>
            <div className={cn.infoItem}>
              <UserOutlined className={cn.icon} />
              <div className={cn.content}>
                <p className={cn.label}>Владелец / Контактное лицо</p>
                {isEditing ? (
                  <Form.Item name="ownerName">
                    <Input placeholder="Введите имя" />
                  </Form.Item>
                ) : (
                  <p className={cn.value}>
                    {data.constructionSiteModel.ownerName || '—'}
                  </p>
                )}
              </div>
            </div>

            <div className={cn.infoItem}>
              <PhoneOutlined className={cn.icon} />
              <div className={cn.content}>
                <p className={cn.label}>Телефон</p>
                {isEditing ? (
                  <Form.Item name="ownerPhoneNumber">
                    <Input placeholder="Введите телефон" />
                  </Form.Item>
                ) : (
                  <p className={cn.value}>
                    {data.constructionSiteModel.ownerPhoneNumber || '—'}
                  </p>
                )}
              </div>
            </div>

            <div className={cn.infoItem}>
              <EnvironmentOutlined className={cn.icon} />
              <div className={cn.content}>
                <p className={cn.label}>Адрес объекта</p>
                {isEditing ? (
                  <Form.Item name="address">
                    <Input placeholder="Введите адрес" />
                  </Form.Item>
                ) : (
                  <p className={cn.value}>
                    {data.constructionSiteModel.address || 'Адрес не указан'}
                  </p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className={cn.formBottomRow}>
                <div className={cn.descriptionWrapper}>
                  <Form.Item name="description" label="Описание объекта">
                    <Input placeholder="Описание" />
                  </Form.Item>
                </div>

                <div className={cn.switchesGroup}>
                  <div className={cn.switchItem}>
                    <label>Активен</label>
                    <Form.Item name="isActive" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>
                  <div className={cn.switchItem}>
                    <label>Основной</label>
                    <Form.Item name="isMain" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>
                  <div className={cn.switchItem}>
                    <label>Склад</label>
                    <Form.Item name="isStore" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Form>

        <div className={cn.ordersSection}>
          <h2 className={cn.subTitle}>
            <ContainerOutlined /> Заказы по данному объекту
          </h2>

          {data.orders?.length ? (
            <>
              {/* Десктопная версия: Таблица */}
              <div className={cn.desktopView}>
                <div
                  className={[
                    cn.table,
                    isOrgSelected ? cn.organisationSelected : '',
                  ].join(' ')}
                >
                  <Table
                    columns={columns}
                    dataSource={data.orders}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              </div>

              {/* Мобильная версия: Карточки */}
              <div className={cn.mobileView}>
                {data.orders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </>
          ) : (
            <div className={cn.empty}>
              По этому объекту заказов пока не найдено
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default ConstructionSite
